<template>
  <div
    class="reputation-badge"
    :class="[`tier-${tier.position}`]"
    :title="tooltipText"
  >
    <span
      class="reputation-badge__symbol"
      v-if="medalIcon"
      v-html="medalIcon"
    ></span>
    <span v-else class="reputation-badge__symbol">{{ tier.symbol }}</span>
    <span v-if="showTierName" class="reputation-badge__name">{{
      tier.name
    }}</span>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { scoreToPercentileSync } from "@/utils/utils.js";
import { getReputationTier } from "@/utils/reputationTiers.js";
import { medalSvgs } from "@/assets/medals.js";
// Map tier names to medalSvgs keys - only for Standard tier set
const tierNameToMedalKey = {
  Bronze: "bronze",
  Silver: "silver",
  Gold: "gold",
  Ruby: "ruby",
  Diamond: "diamond",
};

const medalIcon = computed(() => {
  // Only use medal SVGs for Standard tier set, otherwise use tier symbol
  if (props.tierSet && props.tierSet.name !== "Standard") {
    return null; // Use tier symbol for non-standard sets
  }
  const key = tierNameToMedalKey[tier.value.name];
  return key ? medalSvgs[key] : null;
});

const props = defineProps({
  score: {
    type: Number,
    required: true,
  },
  showTierName: {
    type: Boolean,
    default: false,
  },
  isCurrentUser: {
    type: Boolean,
    default: false,
  },
  tierSet: {
    type: Object,
    default: null,
  },
});

const percentile = computed(() => {
  return Math.round(scoreToPercentileSync(props.score));
});

const tier = computed(() => {
  if (props.tierSet) {
    // Use the provided tier set
    const tiers = props.tierSet.tiers || [];
    return (
      tiers.find((tier) => {
        const [min, max] = tier.percentileRange;
        return percentile.value >= min && percentile.value <= max;
      }) || tiers[0]
    );
  }
  // Fallback to global tier system
  return getReputationTier(percentile.value);
});

const tooltipText = computed(() => {
  // Helper to get tier range string
  function getTierRangeText() {
    let low, high;
    if (tier.value.range) {
      const match = tier.value.range.match(/(\d+)[^\d]+(\d+)/);
      if (match) {
        low = match[1];
        high = match[2];
      }
    } else if (tier.value.percentileRange) {
      if (Array.isArray(tier.value.percentileRange)) {
        low = tier.value.percentileRange[0];
        high = tier.value.percentileRange[1];
      } else if (typeof tier.value.percentileRange === "string") {
        const match = tier.value.percentileRange.match(/(\d+)[^\d]+(\d+)/);
        if (match) {
          low = match[1];
          high = match[2];
        }
      }
    }
    return low !== undefined && high !== undefined
      ? `${tier.value.name} Tier Range: ${low}% - ${high}%`
      : `${tier.value.name} Tier`;
  }

  if (props.isCurrentUser) {
    // 1. Total Score
    const firstLine = `Total Score: ${props.score}`;
    // 2. Above X% of users
    const secondLine = `Above ${percentile.value}% of users`;
    // 3. Tier range
    const tierRangeText = getTierRangeText();
    return `${firstLine}\n${secondLine}\n${tierRangeText}`;
  } else {
    // Show tier range for other users
    return getTierRangeText();
  }
});
</script>

<style scoped lang="scss">
.reputation-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 16px;
  background-color: var(--reputation-badge-bg, rgba(255, 255, 255, 0.1));
  border: 1px solid var(--reputation-badge-border, rgba(255, 255, 255, 0.2));
  cursor: help;

  &__symbol {
    font-size: 1.2em;
  }

  &__name {
    font-size: 0.85em;
    opacity: 0.9;
  }

  &.tier-1 {
    --reputation-badge-bg: rgba(205, 127, 50, 0.1);
    --reputation-badge-border: rgba(205, 127, 50, 0.4);
  }

  &.tier-2 {
    --reputation-badge-bg: rgba(192, 192, 192, 0.1);
    --reputation-badge-border: rgba(192, 192, 192, 0.4);
  }

  &.tier-3 {
    --reputation-badge-bg: rgba(255, 215, 0, 0.1);
    --reputation-badge-border: rgba(255, 215, 0, 0.4);
  }

  &.tier-4 {
    --reputation-badge-bg: rgba(224, 17, 95, 0.1);
    --reputation-badge-border: rgba(224, 17, 95, 0.4);
  }

  &.tier-5 {
    --reputation-badge-bg: rgba(185, 242, 255, 0.1);
    --reputation-badge-border: rgba(185, 242, 255, 0.4);
  }

  &.prominent-badge {
    transform: scale(1.1);
    font-weight: 700;
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.2);
    transition: all 0.2s ease;
    color: var(--foreground-primary);

    &:hover {
      transform: scale(1.15);
      box-shadow: 0 0 12px rgba(255, 255, 255, 0.3);
    }

    .reputation-badge__symbol {
      font-size: 1.3em;
    }

    .reputation-badge__name {
      font-size: 0.9em;
      opacity: 1;
      color: var(--tw-text-opacity, 1);
    }
  }
}
</style>
